import { resolveStripeFlags } from "./stripeFeature";

function berlinDay(date) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** True through the end of the trial's last calendar day (Europe/Berlin). */
function trialDayStillOpen(subscription) {
  const status = String(subscription?.subscriptionStatus || "").toLowerCase();
  const trialing = !!subscription?.trialing || status === "trialing";
  if (!trialing) return false;
  const endIso = subscription?.trialEnd || subscription?.currentPeriodEnd;
  if (!endIso) return true;
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return !!subscription?.active;
  return berlinDay(new Date()) <= berlinDay(end);
}

/**
 * Visual package for accent colors.
 * Purple (io) while Kjuup.io is still entitled, including the last day of a
 * trial and a cancel scheduled for period end. Green (plus) only after the
 * trial/period has actually finished.
 */
export function resolvePackageId(profile, subscription) {
  const { stripeUiEnabled, isKjuupPlusMember } = resolveStripeFlags(
    profile,
    subscription
  );
  const status = String(subscription?.subscriptionStatus || "").toLowerCase();
  const fullyCanceled = status === "canceled" || status === "cancelled";
  const ioPackageActive =
    !!stripeUiEnabled &&
    !fullyCanceled &&
    (!!subscription?.active || trialDayStillOpen(subscription));

  const connectedToKjuupPlus =
    !!isKjuupPlusMember ||
    !!(profile?.companies || []).some((c) => c.isKjuup) ||
    !!profile?.company?.isKjuup ||
    !!profile?.isKjuup;

  if (ioPackageActive) return "io";
  if (connectedToKjuupPlus) return "plus";
  return "kjuup";
}

/** True when the user belongs to a Kjuup+ company (independent of Kjuup.io). */
export function isConnectedToKjuupPlus(profile, subscription) {
  const { isKjuupPlusMember } = resolveStripeFlags(profile, subscription);
  return (
    !!isKjuupPlusMember ||
    !!(profile?.companies || []).some((c) => c.isKjuup) ||
    !!profile?.company?.isKjuup ||
    !!profile?.isKjuup
  );
}

/**
 * Name of the first (main) product entry in the sidebar / cockpit.
 * The Kjuup logo at the very top never changes; only this label reflects
 * the booked package:
 *   "Kjuup"     plain user
 *   "Kjuup+"    member of a Kjuup+ company
 *   "Kjuup.io"  Kjuup.io active (trial or paid), no Kjuup+ company
 *   "Kjuup.io+" Kjuup.io active AND Kjuup+ company member
 */
export function resolvePackageLabel(profile, subscription) {
  const packageId = resolvePackageId(profile, subscription);
  const plus = isConnectedToKjuupPlus(profile, subscription);
  if (packageId === "io") return plus ? "Kjuup.io+" : "Kjuup.io";
  if (plus) return "Kjuup+";
  return "Kjuup";
}
